// === Info Modals (Privacy / Imprint / About) ===

import { pushScreen, back } from '../utils/nav.js';

const INFO_CONTENT = {
  privacy: {
    title: 'Privacy',
    html: `
      <p>This app is designed to respect your privacy. It works entirely in your
      browser — there is no account, no login, and no personal data is sent to
      or stored on any server.</p>

      <h3>What we store</h3>
      <p>Your herd profile (herd type and key seasonal dates), any personal
      adjustments you make (such as moving a task to a different month) and which
      tasks you have marked as complete are saved <strong>locally on your own
      device</strong> using your browser's storage. This data never leaves your
      device and is only used to personalise the advice you see.</p>

      <h3>Hosting</h3>
      <p>The app is delivered by <strong>Cloudflare</strong>, our hosting provider.
      As with any website, your device's IP address is processed for the technical
      purpose of delivering the app to you, and may appear temporarily in
      Cloudflare's server logs for security and operational reasons. We do not use
      this information to identify you.</p>

      <h3>Usage statistics</h3>
      <p>We use <strong>Cloudflare Web Analytics</strong> to understand how the app is
      used &mdash; for example how many people visit and which pages are most useful.
      This is a <strong>privacy-first, cookieless</strong> service: it does not use
      cookies, does not fingerprint your device, and does not track you across other
      websites. It only produces anonymous, aggregate statistics and cannot identify
      you personally.</p>

      <h3>What we don't do</h3>
      <ul>
        <li>We do not use tracking cookies or advertising.</li>
        <li>We do not build a profile of you or identify you personally.</li>
        <li>We do not sell your data or share it with advertisers.</li>
      </ul>

      <h3>Clearing your data</h3>
      <p>You can remove all stored data at any time by clearing your browser's site
      data for this app. Note that this will also reset your saved progress.</p>

      <h3>External links</h3>
      <p>Resource links open third-party websites (such as Animal Health Ireland and
      Teagasc), which have their own privacy policies.</p>

      <h3>Questions</h3>
      <p>For any questions about data and privacy, contact us at
      <a href="mailto:ahi@animalhealthireland.ie">ahi@animalhealthireland.ie</a>.</p>
    `
  },
  imprint: {
    title: 'Imprint',
    html: `
      <p>This Herd Health Advisory Calendar is provided by Animal Health Ireland.</p>

      <h3>Contact</h3>
      <p>
        Animal Health Ireland<br>
        2&ndash;5 The Archways<br>
        Carrick-on-Shannon, Co. Leitrim, N41 WN27<br>
        Ireland<br>
        <a href="tel:+353719671928">+353 (0)71 9671928</a> &middot;
        <a href="mailto:ahi@animalhealthireland.ie">ahi@animalhealthireland.ie</a>
      </p>

      <h3>Responsible for content</h3>
      <p>Jonas Brock, Animal Health Ireland.</p>

      <p>For more about Animal Health Ireland, visit
      <a href="https://animalhealthireland.ie/" target="_blank" rel="noopener">animalhealthireland.ie</a>.</p>
    `
  },
  about: {
    title: 'About this app',
    html: `
      <p>The Herd Health Advisory Calendar gives Irish dairy and beef farmers
      month-by-month herd health guidance, tailored to your herd type and seasonal
      calendar.</p>

      <h3>Veterinary disclaimer</h3>
      <p>The information here is for general guidance only and is <strong>not a
      substitute for professional veterinary advice</strong>. Always consult your
      own vet for diagnosis, treatment and decisions specific to your herd.</p>

      <h3>About the images</h3>
      <p>Some illustrative photographs in this app are AI-generated and are used for
      visual context only. They do not depict specific real animals, farms or people.</p>

      <h3>Resources</h3>
      <p>Links to documents and programmes point to Animal Health Ireland, Teagasc,
      Met Éireann and other trusted sources. We aim to keep these current; please
      use the in-app feedback button to report a broken link.</p>
    `
  }
};

export function setupInfoModals() {
  const overlay = document.getElementById('infoOverlay');
  const titleEl = document.getElementById('infoTitle');
  const bodyEl = document.getElementById('infoBody');
  const btnClose = document.getElementById('btnCloseInfo');
  if (!overlay) return;

  function open(key) {
    const content = INFO_CONTENT[key];
    if (!content) return;
    titleEl.textContent = content.title;
    bodyEl.innerHTML = content.html;
    bodyEl.scrollTop = 0;
    overlay.removeAttribute('hidden');
    document.body.classList.add('search-open');
    btnClose.focus();
    pushScreen(close);
  }

  function close() {
    overlay.setAttribute('hidden', '');
    document.body.classList.remove('search-open');
  }

  btnClose.addEventListener('click', back);
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) back();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !overlay.hasAttribute('hidden')) back();
  });

  // Expose to window.app for the footer onclick handlers.
  if (window.app) window.app.openInfo = open;
}
